import { AlertProps, LinkProps, Theme, alpha } from "@mui/material";
import { RaThemeOptions, defaultTheme } from "react-admin";
import { auBrand } from "./brand";

const { color, font } = auBrand;

const heading = { fontFamily: font.display, fontWeight: 500 } as const;

const arrowBorder = (arrowColor: string) =>
  `${arrowColor} ${arrowColor} transparent transparent`;

/** The brand's corner arrow: a 10px, 3px-stroke bracket off the top-right. */
const cornerArrow = (arrowColor: string) =>
  ({
    content: '""',
    position: "absolute",
    width: 10,
    height: 10,
    left: "calc(100% - 2px)",
    bottom: "calc(100% - 2px)",
    border: "3px solid",
    borderColor: arrowBorder(arrowColor),
    transition: "transform 150ms ease-out, border-color 150ms ease-out",
    pointerEvents: "none",
  } as const);

type AlertStyleProps = { ownerState: AlertProps; theme: Theme };

const alertTone = (ownerState: AlertProps, theme: Theme) =>
  theme.palette[ownerState.color ?? ownerState.severity ?? "success"];

// The orange warning icon is too faint (~2:1) on a light ground, so icons use `dark`.
const alertIcon = (ownerState: AlertProps, theme: Theme) => ({
  "& .MuiAlert-icon": { color: alertTone(ownerState, theme).dark },
});

export const auTheme = {
  ...defaultTheme,
  palette: {
    primary: {
      main: color.signal,
      light: color.orange,
      dark: color.deepRed,
      contrastText: color.ink,
    },
    secondary: {
      main: color.ink,
      contrastText: color.sand,
    },
    error: {
      main: color.destructive,
    },
    warning: {
      main: color.orange,
      contrastText: color.ink,
    },
    info: {
      main: color.ink,
      contrastText: color.sand,
    },
    success: {
      main: color.success,
    },
    text: {
      primary: color.ink,
      secondary: color.inkMuted,
      disabled: color.inkDisabled,
    },
    divider: color.hairline,
    background: {
      default: color.sand,
      paper: color.sand,
    },
  },
  shape: {
    borderRadius: 0,
  },
  typography: {
    fontFamily: font.body,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: heading,
    h2: heading,
    h3: heading,
    h4: heading,
    h5: heading,
    h6: heading,
    button: {
      textTransform: "none",
      letterSpacing: "0.025em",
    },
  },
  components: {
    MuiCard: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        contained: {
          position: "relative",
          "&:hover::before": { transform: "translate(2px, -2px)" },
          "&.Mui-disabled::before": { display: "none" },
        },
        containedPrimary: {
          "&::before": cornerArrow(color.signal),
          "&:hover": {
            backgroundColor: color.deepRed,
            color: color.white,
            "&::before": { borderColor: arrowBorder(color.deepRed) },
          },
        },
        containedSecondary: {
          "&::before": cornerArrow(color.ink),
          "&:hover": {
            backgroundColor: color.signal,
            color: color.ink,
            "&::before": { borderColor: arrowBorder(color.signal) },
          },
        },
        // primary-400 is too light for small text on sand or white.
        textPrimary: { color: color.deepRed },
        outlinedPrimary: { color: color.deepRed },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: LinkProps }) =>
          ownerState.color === "primary"
            ? { color: color.deepRed, "&:hover": { color: color.redOrange } }
            : {},
      },
    },
    MuiAlert: {
      styleOverrides: {
        standard: ({ ownerState, theme }: AlertStyleProps) => ({
          // MUI's default tint is lightened toward white, which reads cold on sand.
          backgroundColor: alpha(alertTone(ownerState, theme).main, 0.08),
          ...alertIcon(ownerState, theme),
        }),
        outlined: ({ ownerState, theme }: AlertStyleProps) =>
          alertIcon(ownerState, theme),
      },
    },
    // MUI's default 8px leaves buttons flush to the edge and clips the corner arrow.
    // Matches DialogContent's horizontal padding.
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          padding: theme.spacing(2, 3),
        }),
      },
    },
    // MUI's default cell border is a lightened divider that vanishes on sand.
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${color.rule}`,
        },
      },
    },
    // Form toolbar (Edit/Create): react-admin defaults to grey[100], which reads cold on sand.
    RaToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: color.sand,
          borderTop: `1px solid ${color.hairline}`,
        },
      },
    },
    // Sidebar menu items
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.RaMenuItemLink-active": {
            fontWeight: 600,
            color: color.ink,
          },
          "&.RaMenuItemLink-active svg": {
            color: color.signal,
          },
        },
      },
    },
  },
} as const satisfies RaThemeOptions;
